import React, {PureComponent} from "react"

import Flex from "@irati/ui/src/Flex"
import {injectIntl, InjectIntl, FormattedMessage} from "@irati/utils/lang"
import {OrganisationalSummaryCategory} from "@irati/types"
import theme from "@irati/ui/src/global/theme"

import Icon, {IconTypes} from "@irati/ui/src/Icon"
import GenericModal from "@irati/ui/src/GenericModal"
import {Body, H3} from "@irati/ui/src/Typography"
import {OverlayPlacement, Tooltip} from "@irati/ui/src/Tooltip"
import {TrendTypes} from "./enums"
import {KeyCircle, OpenModalButton, FullWidth} from "./styled"
import {getModalImg, getModalTitle, getModalText, getOpenModalButtonText} from "./buildModalContext"
import FilterChipToggle from "@irati-web/admin/src/components/FilterChipToggle"

interface Props extends InjectIntl {
  onChange: (trendRate: TrendTypes) => void
  isTeamSelected: boolean
  selectedTrendRate: TrendTypes
  selectedCategory: OrganisationalSummaryCategory
}

interface State {
  showModal: boolean
}

class Categories extends PureComponent<Props, State> {
  state = {
    showModal: false
  }

  getTrendOptions = () => {
    const {selectedCategory, isTeamSelected} = this.props
    let graphFilters = []

    if (selectedCategory === OrganisationalSummaryCategory.Main) {
      graphFilters = [
        {label: "admin.reporting.orgSummary.main.savings.title", id: "mainSavings", value: TrendTypes.savings, metric: "savings"},
        {
          label: "admin.reporting.orgSummary.main.numEngagements.title",
          id: "mainEngagements",
          value: TrendTypes.engagements,
          metric: "engagements"
        }
      ]
    }

    if (selectedCategory === OrganisationalSummaryCategory.Perks) {
      graphFilters = [
        {
          label: "admin.reporting.orgSummary.perks.numUsersRedeemed.title",
          id: "perksUsersRedeemed",
          value: TrendTypes.usersRedeemed,
          metric: "uniqueUsers"
        },
        {
          label: "admin.reporting.orgSummary.perks.numRedemptions.title",
          id: "perksRedemptions",
          value: TrendTypes.redemptions,
          metric: "redemptions"
        },
        {label: "admin.reporting.orgSummary.flexi.burned.title", id: "flexiBurned", value: TrendTypes.burned, metric: "flexiPointsSpent"}
      ]
    }
    if (selectedCategory === OrganisationalSummaryCategory.Celebrations) {
      graphFilters = [
        {
          label: "admin.reporting.orgSummary.celebrations.numUsersEngaged.title",
          id: "celebrationsUsersEngaged",
          value: TrendTypes.usersEngaged,
          metric: "uniqueUsers"
        },
        {
          label: "admin.reporting.orgSummary.celebrations.interactions.title",
          id: "celebrationsInteractions",
          value: TrendTypes.interactions,
          metric: "interactions"
        },
        {
          label: "admin.reporting.orgSummary.celebrations.rewardPoints.title",
          id: "celebrationsRewardPoints",
          value: TrendTypes.rewardPoints,
          metric: "rewardPoints"
        }
      ]
    }
    if (selectedCategory === OrganisationalSummaryCategory.Wellness) {
      graphFilters = [
        {
          label: "admin.reporting.orgSummary.wellness.numUsersEngaged.title",
          id: "wellnessUsersEngaged",
          value: TrendTypes.usersEngaged,
          metric: "uniqueUsers"
        },
        {
          label: "admin.reporting.orgSummary.wellness.numViews.title",
          id: "wellnessViews",
          value: TrendTypes.views,
          metric: "numberOfViews"
        },
        {
          label: "admin.reporting.orgSummary.wellness.numWebinarRegistrations.title",
          id: "wellnessWebinarRegistrations",
          value: TrendTypes.webinarRegistrations,
          metric: "webinarRegistrations"
        }
      ]
    }

    if (isTeamSelected) {
      return graphFilters
    }

    return [
      {
        label: "admin.reporting.orgSummary.main.engagementRate.title",
        id: "trendRatesEngagementRate",
        value: TrendTypes.engagementRate,
        metric: "engagementRate"
      },
      ...graphFilters
    ]
  }

  render() {
    const {formatMessage, onChange, selectedTrendRate, selectedCategory} = this.props
    const {showModal} = this.state
    const trendOptions = this.getTrendOptions()
    const openModalText = getOpenModalButtonText(selectedTrendRate)
    const modalTitle = getModalTitle(selectedTrendRate, selectedCategory)
    const modalImg = getModalImg(selectedTrendRate)
    const modalText = getModalText(selectedTrendRate, selectedCategory)

    return (
      <>
        <Flex
          direction="column"
          marginBottom={4}
          justifyContent="space-between"
          height="100%"
          alignItems="baseline"
          position="relative"
          width="100%">
          <FullWidth>
            <Flex direction="column" gap={theme.spacing.oneAndHalf} width="100%">
              {trendOptions.map((trendRate) => (
                <FilterChipToggle id={trendRate.id} isToggled={selectedTrendRate === trendRate.value} onClick={() => onChange(trendRate)}>
                  {formatMessage({id: trendRate.label})}
                </FilterChipToggle>
              ))}
            </Flex>
            {selectedTrendRate === TrendTypes.savings && (
              <>
                <Body small marginTop={3} marginBottom weight="bold">
                  <FormattedMessage id="admin.reporting.orgSummary.graphFilterKeys.estimatedSavings" />
                </Body>
                <Flex marginLeft={0.5} alignItems="center">
                  <KeyCircle color={theme.colors.primary2} />
                  <Body small marginLeft>
                    <FormattedMessage id="admin.reporting.orgSummary.graphFilterKeys.discountVouchers" />
                  </Body>
                </Flex>
                <Body small weight="bold" marginTop={2} marginBottom>
                  <FormattedMessage id="admin.reporting.orgSummary.graphFilterKeys.fixedSavings" />
                </Body>
                <Flex marginLeft={0.5} marginBottom alignItems="center">
                  <KeyCircle color={theme.colors.utility.gold} />
                  <Body small marginLeft>
                    <FormattedMessage id="admin.reporting.orgSummary.graphFilterKeys.reward" />
                  </Body>
                </Flex>
                <Flex marginLeft={0.5} marginBottom alignItems="center">
                  <KeyCircle color={theme.colors.tertiary.f_400} />
                  <Body small marginLeft>
                    <FormattedMessage id="admin.reporting.orgSummary.graphFilterKeys.flexi" />
                  </Body>
                </Flex>
                <Flex marginLeft={0.5} marginBottom alignItems="center">
                  <KeyCircle color={theme.colors.primary1} />
                  <Body small marginLeft>
                    <FormattedMessage id="admin.reporting.orgSummary.graphFilterKeys.gift" />
                  </Body>
                </Flex>
              </>
            )}
            {selectedTrendRate === TrendTypes.rewardPoints && (
              <>
                <Flex marginTop={3} marginLeft={0.5} marginBottom alignItems="center">
                  <KeyCircle color={theme.colors.utility.gold} />
                  <Body small marginLeft>
                    <FormattedMessage id="admin.reporting.orgSummary.graphFilterKeys.given" />
                  </Body>
                </Flex>
                <Flex marginLeft={0.5} marginBottom alignItems="center">
                  <KeyCircle color={theme.colors.primary2} />
                  <Body small marginLeft>
                    <FormattedMessage id="admin.reporting.orgSummary.graphFilterKeys.purchased" />
                  </Body>
                </Flex>
              </>
            )}
          </FullWidth>
          {openModalText && (
            <OpenModalButton cta small onClick={() => this.setState({showModal: true})}>
              <FormattedMessage id={openModalText} />
              <Icon marginLeft={0.5} icon={IconTypes.circleQuestion}></Icon>
            </OpenModalButton>
          )}
          {selectedTrendRate === TrendTypes.redemptions && (
            <Tooltip
              placement={OverlayPlacement.Top}
              tooltipChildren={
                <Body small padding={[1, 1]}>
                  <FormattedMessage id="admin.reporting.orgSummary.modal.redemptions.tooltip" />
                </Body>
              }>
              <OpenModalButton cta small>
                <FormattedMessage id="admin.reporting.orgSummary.modal.openModalText.redemptions" />
                <Icon marginLeft={0.5} icon={IconTypes.circleQuestion}></Icon>
              </OpenModalButton>
            </Tooltip>
          )}
        </Flex>
        <GenericModal show={showModal} hideActions onHide={() => this.setState({showModal: false})}>
          <Flex justifyContent="center">
            <H3 marginBottom={3} textAlign="center">
              <FormattedMessage id={modalTitle} />
            </H3>
          </Flex>
          <Flex justifyContent="center" marginBottom={3}>
            <img width="120px" height="120px" src={formatMessage({id: modalImg})} />
          </Flex>
          <Body textAlign="center">{modalText}</Body>
        </GenericModal>
      </>
    )
  }
}

export default injectIntl(Categories)
